import * as endPoints from "../constants/EndPoints";
import * as types from "../constants/ActionTypes";
import { PRESSURE_DISPLAY_MODE } from "../constants/Misc";
import { fetchMiddleware } from "../actions/MiddlewareActions";

export const parseTransient = (data, pressureCoefficient, yAxisCoefficient) => {
    let transientData = null;
    const recordData = data.split('-');

    const dateAndTime = recordData[0].split(' ');
    const values = recordData[3].split(',');

    const dateAsDayMonthYear = dateAndTime[0].split(':');
    const timeAsHourMinutsSeconds = dateAndTime[1].split(':');

    const time = new Date(Date.UTC(
      dateAsDayMonthYear[2],          // Year
      (dateAsDayMonthYear[1] - 1),    // Month
      dateAsDayMonthYear[0],          // Day
      timeAsHourMinutsSeconds[0],     // Hour
      timeAsHourMinutsSeconds[1],     // Minute
      timeAsHourMinutsSeconds[2])     // Seconds
    );

    const transientValues = values.map((val) => parseInt(val) * pressureCoefficient + yAxisCoefficient);

    transientData = {
        x: time.getTime(),
        y: transientValues,
    };

    return (transientData);
};

const getSelectedProjectFromState = (state) => {
  const selectedProjectId = state.leaksList.selectedProject;
  const projectsList = state.leaksList.projectsList.items;
  const projectsIndexMap = state.leaksList.projectsList.projectIndexMap;
  const selectedProjectObj = projectsList[projectsIndexMap[selectedProjectId]];
  return selectedProjectObj;
}

const parsePressureData = (data) => {
    let cRc = null;

    const recordData = data.split('-');

    const dateAndTime = recordData[0].split(' ');
    const interval = parseInt(recordData[1]);
    const count = parseInt(recordData[2]);
    const values = recordData[3].split(',');

    const dateAsDayMonthYear = dateAndTime[0].split(':');
    const timeAsHourMinutsSeconds = dateAndTime[1].split(':');

    const startTime = new Date(Date.UTC(
      dateAsDayMonthYear[2],          // Year
      (dateAsDayMonthYear[1] - 1),    // Month
      dateAsDayMonthYear[0],          // Day
      timeAsHourMinutsSeconds[0],     // Hour
      timeAsHourMinutsSeconds[1],     // Minute
      timeAsHourMinutsSeconds[2])     // Seconds
    );

    cRc = {
        startTime,
        interval,
        count,
        values
    };

    return (cRc);
};

export const fetchPressureTransient = (sensor, deviceId, DaysBack) => {
  return (dispatch, getState) => {
    const state = getState();
    const daysStep = (state.leaksList.pressure.mode === PRESSURE_DISPLAY_MODE.DAY) ? 1 : 7;
    const path = endPoints.SENSOR_PRS_ENDPOINT + `/prsTransient/${sensor.id}/${DaysBack + daysStep}/${DaysBack}`;

    dispatch(requestSensorTran(deviceId));

    return fetchMiddleware(path, {}, getState).then((json) => {
      let transientData = [];

      if (json.status && json.data && json.data.length > 0) {
        const factorCoeff = sensor.pressure_calib_coeff || 1;
        const yAxisCoeff = sensor.pressure_calib_coeff_y_axis || 0;
        const allData = json.data;

        allData.forEach((rawData) => {
          const records = rawData.Data.split('~');
          records.forEach((record) => {
            const tranData = parseTransient(record, factorCoeff, yAxisCoeff);
            if (tranData != null) {
              transientData.push(tranData);
            }
          });
        });
        transientData = transientData.sort((a, b) => a.x - b.x);
      }
      dispatch(receiveSensorTran(deviceId, transientData));
    });
  };
};

const requestSensorTran = (deviceId) => {
  return {
    type: types.REQUEST_SENSOR_TRAN,
    deviceId,
  };
};

const receiveSensorTran = (deviceId, data) => {
  return {
    type: types.RECEIVE_SENSOR_TRAN,
    deviceId,
    data,
  };
};

export const fetchPressure = (sensor, deviceId, DaysBack) => {
  return (dispatch, getState) => {
    const state = getState();
    const daysStep = (state.leaksList.pressure.mode === PRESSURE_DISPLAY_MODE.DAY) ? 1 : 7;
    const path = endPoints.SENSOR_PRS_ENDPOINT + `/prsTrend/${sensor.id}/${DaysBack}/${DaysBack + daysStep}`;

    dispatch(requestSensorPrs(deviceId, DaysBack));

    return fetchMiddleware(path, {}, getState).then((json) => {
      let readyData = {};
      let avgData = {};

      if (json.status && json.data && json.data.length > 0) {
        const selectedProjectObj = getSelectedProjectFromState(state);
        const allData = json.data;

        let readyDataX = [];
        let readyDataY = [];
        let tempDataBuilder = [];
        let tempAvgDataBuilder = [];

        allData.forEach((rawData) => {
            const records = rawData.Data.split('~');
            records.forEach((record) => {
              const pressureData = parsePressureData(record);
              if (pressureData != null) {
                const { values, startTime, interval } = pressureData;
                const avgIntervalMillis = 1000 * 60 * 15; // 15 minutes
                let referenceTime = startTime.getTime();
                let mutableValue = 0;
                let mutableCount = 0;

                values.forEach((val, index) => {
                  const factorCoeff = sensor.pressure_calib_coeff || 1;
                  const yAxisCoeff = sensor.pressure_calib_coeff_y_axis || 0;
                  const value = parseInt(val) * factorCoeff + yAxisCoeff;
                  const time = startTime.getTime() + (interval * index) * 1000;

                  if (referenceTime + avgIntervalMillis > time) {
                    // add the values
                    mutableValue += value;
                    mutableCount += 1;
                  } else {
                    const xVal = (time + referenceTime) / 2;
                    const yVal = mutableValue / mutableCount;

                    tempAvgDataBuilder.push({
                      x: xVal,
                      y: yVal
                    });

                    referenceTime = time;
                    mutableCount = 0;
                    mutableValue = 0;
                  }

                  tempDataBuilder.push({
                    x: time,
                    y: value,
                  });
                });
              }
            });
          });

        tempAvgDataBuilder = tempAvgDataBuilder.sort((a, b) => a.x - b.x);
        avgData = {
          x: tempAvgDataBuilder.map(item => item.x),
          y: tempAvgDataBuilder.map(item => item.y),
        };

        tempDataBuilder = tempDataBuilder.sort((a, b) => a.x - b.x);
        tempDataBuilder.forEach((item) => {
          readyDataX.push(item.x);
          readyDataY.push(item.y);
        });

        readyData = {
          x: readyDataX,
          y: readyDataY
        };
      }
      dispatch(receiveSensorPrs(deviceId, readyData, avgData));
    });
  };
};

const requestSensorPrs = (deviceId, daysBack) => {
  return {
    type: types.REQUEST_SENSOR_PRS,
    deviceId,
    daysBack,
  };
};

const receiveSensorPrs = (deviceId, data, avgData) => {
  return {
    type: types.RECEIVE_SENSOR_PRS,
    deviceId,
    data,
    avgData,
  };
};

export const changeDisplayMode = (sensor, deviceId, mode) => {
  const action = {
    type: types.CHANGE_PRESSURE_DISPLAY_MODE,
    mode,
  };
  return (dispatch) => {
    dispatch(action);
    dispatch(fetchPressure(sensor, deviceId, 0));
    dispatch(fetchPressureTransient(sensor, deviceId, 0));
  };
};

export const fetchPressureReading = (projectId, loggerId, hoursBack = 0) => {
  return (dispatch, getState) => {
    const fromHour = hoursBack + 24;
    const toHour = hoursBack;
    const path = endPoints.SENSORS_PRESSURE_ENDPOINT + `/${projectId}/${loggerId}/reading?from_hour=${fromHour}&to_hour=${toHour}`;

    dispatch(requestPressureReading(loggerId, hoursBack));

    return fetchMiddleware(path, {}, getState).then((json) => {
      dispatch(receivePressureReading(loggerId, json.data || [], hoursBack));
    });
  };
};

export const fetchMultiDayReading = (projectId, loggerId, startDate, endDate) => {
  return (dispatch, getState) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Set end to end of day
    end.setHours(23, 59, 59, 999);

    const fromHour = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60));
    const toHour = Math.max(0, Math.floor((now.getTime() - end.getTime()) / (1000 * 60 * 60)));

    const path = endPoints.SENSORS_PRESSURE_ENDPOINT + `/${projectId}/${loggerId}/reading?from_hour=${fromHour}&to_hour=${toHour}`;

    return fetchMiddleware(path, {}, getState).then((json) => {
      return json.data || [];
    });
  };
};

const requestPressureReading = (loggerId, hoursBack) => {
  return {
    type: types.REQUEST_PRESSURE_READING,
    loggerId,
    hoursBack,
  };
};

const receivePressureReading = (loggerId, data, hoursBack) => {
  return {
    type: types.RECEIVE_PRESSURE_READING,
    loggerId,
    data,
    hoursBack,
  };
};

export const fetchMultiDayTransients = (projectId, loggerId, startDate, endDate) => {
  return (dispatch, getState) => {
    const path = endPoints.SENSORS_PRESSURE_ENDPOINT + `/${projectId}/${loggerId}/transients?from=${startDate}&to=${endDate}`;

    return fetchMiddleware(path, {}, getState).then((json) => {
      return json.data || [];
    });
  };
};

export const fetchPressureAlarmsForSensor = (projectId, loggerId) => {
  return (dispatch, getState) => {
    const path = `${endPoints.SENSORS_PRESSURE_ENDPOINT}/${projectId}/${loggerId}/alarms`;

    return fetchMiddleware(path, {}, getState)
      .then((json) => {
        if (Array.isArray(json)) return json;
        return json?.data || [];
      })
      .catch(() => []);
  };
};

export const fetchPressureTransients = (projectId, loggerId) => {
  return (dispatch, getState) => {
    const today = new Date(); // Gets the current date and time
    const from30DaysAgo = new Date(today); // Create a new date object to avoid modifying 'today'

    // Subtract 30 days by setting the date to the current date minus 30
    from30DaysAgo.setDate(today.getDate() - 30);

    const path = endPoints.SENSORS_PRESSURE_ENDPOINT + `/${projectId}/${loggerId}/transients?from=${from30DaysAgo.toISOString().split('T')[0]}`;

    dispatch(requestPressureTransients(loggerId));

    return fetchMiddleware(path, {}, getState).then((json) => {
      dispatch(receivePressureTransients(loggerId, json.data || []));
    });
  };
};

const requestPressureTransients = (loggerId) => {
  return {
    type: types.REQUEST_PRESSURE_TRANSIENTS,
    loggerId,
  };
};

const receivePressureTransients = (loggerId, data) => {
  return {
    type: types.RECEIVE_PRESSURE_TRANSIENTS,
    loggerId,
    data,
  };
};

// Pull the transient severity thresholds out of the project's pressure settings.
// The endpoint returns global defaults (project_id = -1) plus any project-specific
// overrides; project-specific rows win over the defaults for the same param_name.
const parsePressureSettings = (rows, projectId) => {
  const pick = {};
  (rows || []).forEach((row) => {
    const name = row.param_name;
    if (!name) return;
    const isProjectSpecific = String(row.project_id) === String(projectId);
    if (pick[name] === undefined || isProjectSpecific) {
      pick[name] = { value: row.value, isProjectSpecific };
    }
  });
  const num = (name) => {
    const n = parseFloat(pick[name]?.value);
    return isNaN(n) ? undefined : n;
  };
  return {
    med: num('transient_med_threshold_m'),
    high: num('transient_high_threshold_m'),
  };
};

export const fetchPressureSettings = (projectId) => {
  return (dispatch, getState) => {
    const path = `${endPoints.SENSORS_PRESSURE_ENDPOINT}/settings/${projectId}`;

    return fetchMiddleware(path, {}, getState)
      .then((json) => {
        const rows = Array.isArray(json) ? json : (json?.data || []);
        dispatch(receivePressureSettings(parsePressureSettings(rows, projectId)));
      })
      .catch(() => {});
  };
};

const receivePressureSettings = (thresholds) => {
  return {
    type: types.RECEIVE_PRESSURE_SETTINGS,
    thresholds,
  };
};

export const fetchPressureTransientReading = (projectId, sensorId, readingId) => async (dispatch, getState) => {
  const path = endPoints.SENSORS_PRESSURE_ENDPOINT + `/${projectId}/${sensorId}/transient/reading/${readingId}`;

  const response = await fetchMiddleware(path, {}, getState)
  return response;
};

